import { isReelDeliverable, type CampaignPipeline, type PostStatus, type Post, type RosterEntryFinalized } from '@discovr/types';
import { resolvePipeline } from '../utils/campaignPipeline';

/** Lifecycle ordering for the Finalized sub-tab gating. */
export const POST_STATUS_RANK: Record<PostStatus, number> = {
  script_pending: 1,
  script_review: 2,
  content_pending: 3,
  content_review: 4,
  live_pending: 5,
  live: 6,
};

/** The single main production record (V1: length 1). Null-guarded even under
 *  hard cutover — a finalized row missing its post degrades to "no actions". */
export const mainPost = (c: RosterEntryFinalized): Post | undefined => c.posts?.[0];

export const postRank = (c: RosterEntryFinalized): number => {
  const p = mainPost(c);
  return p ? (POST_STATUS_RANK[p.status] ?? 0) : 0;
};

/** "View Script" appears once a script has been submitted (script_review+).
 *  On a campaign with the script lane off it additionally needs the artifact
 *  itself, so rows that skipped the lane never show an empty thumbnail. */
export const canViewScript = (c: RosterEntryFinalized, pipeline?: CampaignPipeline | null): boolean => {
  if (postRank(c) < POST_STATUS_RANK.script_review) return false;
  return resolvePipeline(pipeline).script_enabled || !!mainPost(c)?.script;
};

/** "View Content" appears once the content is APPROVED (live_pending+).
 *  On a campaign with the content lane off it additionally needs the artifact. */
export const canViewContent = (c: RosterEntryFinalized, pipeline?: CampaignPipeline | null): boolean => {
  if (postRank(c) < POST_STATUS_RANK.live_pending) return false;
  return resolvePipeline(pipeline).content_enabled || !!mainPost(c)?.content;
};

export type FinalizedPhase = 'script' | 'content' | 'live';

/** Finalized phase pills the campaign's pipeline can produce. */
export const visibleFinalizedPhases = (pipeline?: CampaignPipeline | null): FinalizedPhase[] => {
  const p = resolvePipeline(pipeline);
  const phases: FinalizedPhase[] = [];
  if (p.script_enabled) phases.push('script');
  if (p.content_enabled) phases.push('content');
  phases.push('live');
  return phases;
};

/** Keeps "all" and the pills whose phase the pipeline can produce. */
export const filterFinalizedPills = <T extends { key: string }>(
  pills: readonly T[],
  pipeline?: CampaignPipeline | null,
): T[] => {
  const phases: string[] = visibleFinalizedPhases(pipeline);
  return pills.filter((pill) => pill.key === 'all' || phases.includes(pill.key));
};

/** The content shape ('short' / 'reel') a deliverable commits to, so the URL
 *  modals can block a mismatched link client-side, as the BE does. */
export function deliverableContentKind(type: string | undefined): string | undefined {
  if (!type) return undefined;
  if (type === 'youtube_shorts') return 'short';
  if (isReelDeliverable(type)) return 'reel';
  return undefined;
}
